import React, { useRef, useState } from 'react';
import styles from './StateSearchBar.module.scss';
import { Container } from '@src/elements/Grid';
import { COLOR_LIME, COLOR_NEUTRAL_DARKEST } from '@src/types/colors';
import BoxIcon from '@src/elements/BoxIcon';
import { useClickOutside } from '@src/hooks/useClickOutside';
import { ButtonLinkStandard } from '../ButtonLink';
import classNames from 'classnames';
import { useViewport } from '@src/hooks/useViewport';
import { SM_BREAKPOINT } from '@src/constants/breakpoints';
import { useRouter } from 'next/router';
import Visibility from '@src/elements/Visibility';
import { ButtonStandard } from '../Button';

// TODO remove hardcoded states in next redesign phase
const STATES = [
  {
    id: 'act',
    label: 'Australian Capital Territory'
  },
  {
    id: 'nsw',
    label: 'New South Wales'
  },
  {
    id: 'qld',
    label: 'Queensland'
  },
  {
    id: 'sa',
    label: 'South Australia'
  },
  {
    id: 'vic',
    label: 'Victoria'
  }
];

export type StateSearchDropdownItem = {
  label: string;
  id: string;
};

interface DropdownNonNative {
  options: StateSearchDropdownItem[];
  selectedItem: StateSearchDropdownItem | undefined;
  setSelectedItem: React.Dispatch<
    React.SetStateAction<StateSearchDropdownItem | undefined>
  >;
  isMobile: boolean;
}

interface StateSearchBar {
  onStateChange?: (selectedState?: StateSearchDropdownItem) => void;
  mapListingVariant?: boolean;
}

const DropdownNonNative: React.FC<DropdownNonNative> = ({
  options,
  selectedItem,
  setSelectedItem,
  isMobile
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isRouting, setIsRouting] = useState<boolean>(false);

  const router = useRouter();

  const handleChange = (item: StateSearchDropdownItem) => {
    setSelectedItem(item);
    setIsOpen(false);

    if (isMobile) {
      setIsRouting(true);
      setTimeout(() => {
        router.push(`/find-your-community/${item.id}`);
      }, 100);
    }
  };

  useClickOutside(dropdownRef, (e) => {
    e.stopPropagation();
    setIsOpen(false);
  });

  return (
    <div
      ref={dropdownRef}
      aria-label="Dropdown menu"
      className={styles.dropdownNonNative}
    >
      <button
        aria-label="Toggle dropdown"
        aria-haspopup="true"
        aria-expanded={isOpen}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={styles.dropdownNonNativeInput}
      >
        <span className={styles.dropdownNonNativeInputContent}>
          <BoxIcon name="search" color={COLOR_NEUTRAL_DARKEST} />
          {selectedItem?.label || 'Find your nearest community'}
        </span>
        <BoxIcon
          name={isRouting ? 'loader' : 'chevron-down'}
          animation={isRouting ? 'bx-spin' : undefined}
          color={COLOR_NEUTRAL_DARKEST}
          className={classNames(styles.chevron, {
            [styles.chevronOpen]: isOpen
          })}
        />
      </button>
      <ul
        role="menu"
        aria-labelledby={'dropdown menu'}
        aria-orientation="vertical"
        className={classNames(styles.dropdownNonNativeUl, {
          [styles.dropdownNonNativeUlOpen]: isOpen
        })}
      >
        {options.map(({ id, label }) => (
          <li key={id} onClick={() => handleChange({ id, label })}>
            <span aria-label={label}>{label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const StateSearchBar: React.FC<StateSearchBar> = ({
  onStateChange,
  mapListingVariant = false
}) => {
  const [selectedItem, setSelectedItem] = useState<
    StateSearchDropdownItem | undefined
  >(undefined);
  const { width } = useViewport();

  return (
    <div
      className={classNames(styles.barWrapper, {
        [styles.mapListingVariant]: mapListingVariant
      })}
    >
      <Container>
        <div className={styles.barContainer}>
          <DropdownNonNative
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
            options={STATES}
            isMobile={width < SM_BREAKPOINT}
          />

          <Visibility xs={false} sm>
            {onStateChange ? (
              <ButtonStandard
                data-testid="stateSearchBarButton"
                color={COLOR_LIME}
                aria-label="Let's go"
                size="md"
                onClick={() => onStateChange(selectedItem)}
              >
                Let&apos;s go
                {/* TODO: move to ButtonStandard "icon" prop once that's switched to BoxIcon */}
                <BoxIcon name="right-arrow-alt" size="sm" />
              </ButtonStandard>
            ) : (
              <ButtonLinkStandard
                href={
                  selectedItem
                    ? `/find-your-community/${selectedItem?.id}`
                    : '/find-your-community'
                }
                data-testid="stateSearchBarButton"
                color={COLOR_LIME}
                aria-label="Let's go"
                size="md"
              >
                Let&apos;s go
                {/* TODO: move to ButtonStandard "icon" prop once that's switched to BoxIcon */}
                <BoxIcon name="right-arrow-alt" size="sm" />
              </ButtonLinkStandard>
            )}
          </Visibility>
        </div>
      </Container>
    </div>
  );
};

export default StateSearchBar;
